/**
 * LLM Processing Service
 * Handles the case processing workflow with progress monitoring
 */

import { api } from './api';
import type { 
  CaseProcessingRequest, 
  ProcessingStatus, 
  DynamicCase 
} from '../types';

export interface ProcessingOptions {
  pollInterval?: number;
  maxRetries?: number;
  onProgress?: (status: ProcessingStatus) => void;
  onError?: (error: Error) => void;
}

export interface ProcessingResult {
  success: boolean;
  requiresReview?: boolean;
  case?: DynamicCase;
  error?: string;
  processingId: string;
  finalStatus: ProcessingStatus;
}

class ProcessingService {
  private activeProcesses = new Map<string, boolean>();

  /**
   * Process a case with full monitoring
   */
  async processCase(
    request: CaseProcessingRequest,
    options: ProcessingOptions = {}
  ): Promise<ProcessingResult> {
    const {
      pollInterval = 2000,
      maxRetries = 3,
      onProgress,
      onError
    } = options;

    let processingId: string;
    let retryCount = 0;

    try {
      // Start processing
      console.log('🤖 Starting case processing:', request);
      const response = await api.processCase(request);
      processingId = response.processingId;
      
      this.activeProcesses.set(processingId, true);

      // Monitor progress
      return await this.monitorProgress(
        processingId,
        pollInterval,
        maxRetries,
        onProgress,
        onError
      );

    } catch (error: any) {
      console.error('❌ Failed to start processing:', error);
      
      if (onError) {
        onError(error);
      }

      return {
        success: false,
        error: error.message || 'Failed to start processing',
        processingId: '',
        finalStatus: {
          status: 'failed',
          progress: 0,
          currentStep: 'Failed to start',
          errors: [error.message]
        }
      };
    }
  }

  /**
   * Monitor processing progress
   */
  private async monitorProgress(
    processingId: string,
    pollInterval: number,
    maxRetries: number,
    onProgress?: (status: ProcessingStatus) => void,
    onError?: (error: Error) => void
  ): Promise<ProcessingResult> {
    let retryCount = 0;

    return new Promise((resolve) => {
      const poll = async () => {
        // Check if process was cancelled
        if (!this.activeProcesses.get(processingId)) {
          resolve({
            success: false,
            error: 'Processing cancelled',
            processingId,
            finalStatus: {
              status: 'failed',
              progress: 0,
              currentStep: 'Cancelled',
            }
          });
          return;
        }

        try {
          const status = await api.getProcessingStatus(processingId);
          
          console.log(`🔄 Processing ${processingId}: ${status.status} (${status.progress}%)`);
          
          // Call progress callback
          if (onProgress) {
            onProgress(status);
          }

          if (status.status === 'completed' && status.caseId) {
            // Processing completed successfully
            try {
              const completedCase = await api.getCase(status.caseId);
              this.activeProcesses.delete(processingId);
              
              resolve({
                success: true,
                case: completedCase,
                processingId,
                finalStatus: status
              });
            } catch (caseError: any) {
              console.error('❌ Failed to fetch completed case:', caseError);
              
              resolve({
                success: false,
                error: 'Processing completed but failed to fetch case',
                processingId,
                finalStatus: status
              });
            }
          } else if (status.status === 'failed') {
            // Processing failed
            this.activeProcesses.delete(processingId);
            const errorMessage = status.errors?.join(', ') || 'Processing failed';
            
            if (onError) {
              onError(new Error(errorMessage));
            }
            
            resolve({
              success: false,
              error: errorMessage,
              processingId,
              finalStatus: status
            });
          } else if (status.status === 'review_required') {
            // Case was created but flagged for manual review
            this.activeProcesses.delete(processingId);

            resolve({
              success: false,
              requiresReview: true,
              processingId,
              finalStatus: status
            });
          } else {
            // Still processing, continue polling
            setTimeout(poll, pollInterval);
          }

          // Reset retry count on successful API call
          retryCount = 0;

        } catch (error: any) {
          retryCount++;
          console.warn(`⚠️ Failed to get processing status (attempt ${retryCount}/${maxRetries}):`, error);
          
          if (retryCount >= maxRetries) {
            this.activeProcesses.delete(processingId);
            
            if (onError) {
              onError(error);
            }
            
            resolve({
              success: false,
              error: `Failed to monitor processing after ${maxRetries} retries`,
              processingId,
              finalStatus: {
                status: 'failed',
                progress: 0,
                currentStep: 'Monitoring failed',
                errors: [error.message]
              }
            });
          } else {
            // Retry with exponential backoff
            setTimeout(poll, pollInterval * Math.pow(2, retryCount - 1));
          }
        }
      };

      // Start polling
      poll();
    });
  }

  /**
   * Cancel active processing
   */
  cancelProcessing(processingId: string): void {
    console.log('🛑 Cancelling processing:', processingId);
    this.activeProcesses.set(processingId, false);
  }

  /**
   * Check if processing is active
   */
  isProcessingActive(processingId: string): boolean {
    return this.activeProcesses.get(processingId) === true;
  }

  /**
   * Get all active processing IDs
   */
  getActiveProcesses(): string[] {
    return Array.from(this.activeProcesses.entries())
      .filter(([_, active]) => active)
      .map(([id, _]) => id);
  }

  /**
   * Process multiple cases in batch
   */
  async processBatch(
    requests: CaseProcessingRequest[],
    options: ProcessingOptions = {}
  ): Promise<ProcessingResult[]> {
    const results: ProcessingResult[] = [];
    
    // Process in parallel with limited concurrency
    const concurrency = 3;
    for (let i = 0; i < requests.length; i += concurrency) {
      const batch = requests.slice(i, i + concurrency);
      const batchPromises = batch.map(request => 
        this.processCase(request, options)
      );
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }
    
    return results;
  }

  /**
   * Validate processing request
   */
  validateRequest(request: CaseProcessingRequest): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!request.fieldId || request.fieldId.trim().length === 0) {
      errors.push('Field ID is required');
    }
    
    if (!request.rawContent || request.rawContent.trim().length < 25) {
      errors.push('Raw content must be at least 25 characters');
    }
    
    if (request.rawContent && request.rawContent.length > 20000) {
      errors.push('Raw content cannot exceed 20,000 characters');
    }
    
    if (request.promptInstructions && request.promptInstructions.length > 4000) {
      errors.push('Prompt instructions cannot exceed 4,000 characters');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get processing statistics
   */
  getProcessingStats(): {
    activeCount: number;
    totalProcessed: number;
  } {
    const activeCount = this.getActiveProcesses().length;
    // In a real implementation, you might track total processed in localStorage or a store
    
    return {
      activeCount,
      totalProcessed: 0 // Would be tracked in real implementation
    };
  }

  /**
   * Clean up completed/failed processes
   */
  cleanup(): void {
    const toDelete: string[] = [];
    
    for (const [processingId, active] of this.activeProcesses.entries()) {
      if (!active) {
        toDelete.push(processingId);
      }
    }
    
    toDelete.forEach(id => this.activeProcesses.delete(id));
    
    console.log(`🧹 Cleaned up ${toDelete.length} inactive processes`);
  }
}

// Export singleton instance
export const processingService = new ProcessingService();

// Export convenience functions
export const processCase = (request: CaseProcessingRequest, options?: ProcessingOptions) => 
  processingService.processCase(request, options);

export const cancelProcessing = (processingId: string) => 
  processingService.cancelProcessing(processingId);

export const isProcessingActive = (processingId: string) => 
  processingService.isProcessingActive(processingId);

export const validateProcessingRequest = (request: CaseProcessingRequest) => 
  processingService.validateRequest(request);